import { getBackgroundColor } from 'src/core/domain/avatarColors';
import { DEFAULT_NAME, PAGE_SIZE } from 'src/core/domain/constants';
import { getPagePaths } from 'src/core/domain/pages';
import { convertToSlug, formattingThreadSlug } from 'src/core/utils/urlUtil';
import type {
  ThreadDetailQuery,
  CategorySeekMaxThreadsQuery,
  SeekMaxThreadCommentsQuery,
  SeekMaxRepliesQuery,
  FeaturedSeekMaxThreadsV3Query,
} from 'src/generated/gql/graphql';
import type { CareerHubLanguage } from 'src/types';
import type { RequiredCategorySeekMaxThreads } from 'src/types/threads';

export interface MemberCount {
  count: number;
  label: string;
}

export interface CommunityGroup {
  groupId: string;
  categoryId: string;
  name: string;
  memberCount: MemberCount;
  url: string;
}

export interface ExactSocialData {
  likeCount: number;
  likeCountLabel: string;
  commentAndReplyCountLabel: string;
  commentCount: number;
  commentCountLabel: string;
}

export interface ExactSocialCommentData {
  likeCountLabel: string;
  replyCount: number;
}

export interface PollOption {
  id: string;
  title: string;
  count: number;
  percentage: number;
  voted: boolean;
}

export interface PollVoteCount {
  count: number;
  label: string;
}

export interface Poll {
  id: string;
  ended: boolean;
  maxVotes: number;
  voteCountLabel: string;
  endDate: string;
  options: PollOption[];
}

export interface Author {
  id: string;
  learningProfileHashCode: number;
  firstName: string;
  jobTitle?: string;
  groups?: string[];
  isVerified: boolean;
  avatarColor: string;
  avatarCharacter: string;
}

export interface MainGroupThread {
  id: string;
  description: string;
  title: string;
  slug: string;
  author: Author;
  attachments: string[];
  exactSocialData: ExactSocialData;
  creationDate: string;
  poll?: Poll;
  liked: boolean;
  totalComments: number;
  url: string;
}

export interface Comment {
  id: string;
  content: string;
  author: Author;
  creationDate: string;
  exactSocialData: ExactSocialCommentData;
  liked: boolean;
}

export interface Reply {
  id: string;
  content: string;
  author: Author;
  creationDate: string;
}

export interface Thread extends MainGroupThread {
  category: CommunityGroup;
  comments: Comment[];
  totalComments: number;
  shareLink: string;
}

export interface ThreadCard extends MainGroupThread {
  category: CommunityGroup;
  totalComments: number;
}

export interface PageInfo {
  endCursor?: number;
  hasNextPage: boolean;
}

export interface CategoryThreadsResult {
  total: number;
  totalPage: number;
  category: CommunityGroup;
  threads: MainGroupThread[];
  pageInfo: PageInfo;
}

type GraphAuthorObject = NonNullable<
  ThreadDetailQuery['seekMaxThread']
>['author'];

const mapAuthor = (author?: GraphAuthorObject): Author => ({
  id: author?.id || '',
  learningProfileHashCode: author?.learningProfileHashCode || 6,
  firstName: author?.learningProfile?.firstName || DEFAULT_NAME,
  jobTitle: author?.learningProfile?.jobTitle || '',
  groups:
    author?.groups?.map(
      ({ description: groupDescription }) => groupDescription,
    ) || [],
  isVerified: (author?.groups && author.groups.length > 0) || false,
  avatarColor: getBackgroundColor(author?.learningProfileHashCode),
  avatarCharacter: author?.learningProfile?.firstName?.[0] || DEFAULT_NAME[0],
});

type GraphExactSocialObject = NonNullable<
  ThreadDetailQuery['seekMaxThread']
>['exactSocialData'];

const mapExactSocialData = ({
  likeCount,
  commentAndReplyCount,
  commentCount,
}: GraphExactSocialObject): ExactSocialData => ({
  likeCount: likeCount?.count || 0,
  likeCountLabel: likeCount?.label || '0',
  commentAndReplyCountLabel: commentAndReplyCount?.label || '0',
  commentCount: commentCount?.count || 0,
  commentCountLabel: commentCount?.label || '0',
});

type GraphExactSocialCommentObject = NonNullable<
  ThreadDetailQuery['seekMaxComments']
>['comments'][number]['exactSocialData'];

const mapExactSocialCommentData = ({
  likeCount,
  replyCount,
}: GraphExactSocialCommentObject): ExactSocialCommentData => ({
  likeCountLabel: likeCount?.label || '0',
  replyCount: replyCount?.count || 0,
});

type GraphPollObject = NonNullable<
  NonNullable<ThreadDetailQuery['seekMaxThread']>['pollV3']
>;

const mapPollData = (pollV3?: GraphPollObject | null): Poll | undefined => {
  if (!pollV3) return undefined;
  return {
    id: pollV3.id,
    ended: pollV3.ended,
    maxVotes: pollV3.maxVotes,
    voteCountLabel: pollV3.voteCount.label,
    endDate: pollV3.endDate.label,
    options: pollV3.options.map(
      ({ id: optionId, title, count, percentage, personalisedDetails }) => ({
        id: optionId,
        title,
        count,
        percentage,
        voted: personalisedDetails?.votedV3 || false,
      }),
    ),
  };
};

type GraphCategoryObject = NonNullable<
  NonNullable<
    NonNullable<
      CategorySeekMaxThreadsQuery['categorySeekMaxThreads']
    >['edges'][number]['node']
  >['categoryGroup']
>;

export const mapCategory = (
  language: CareerHubLanguage,
  category?: GraphCategoryObject,
): CommunityGroup => {
  if (!category)
    return {
      groupId: '',
      categoryId: '',
      name: '',
      memberCount: {
        count: 0,
        label: '',
      },
      url: `${getPagePaths({ language }).CommunityGroups}`,
    };
  const { groupId, categoryId, name, memberCount } = category;
  const url = `${
    getPagePaths({
      language,
      pathParams: { groupSlug: `${convertToSlug(name || '')}-${categoryId}` },
    }).communityGroupsDetails
  }`;
  return {
    groupId,
    categoryId,
    name,
    memberCount,
    url,
  };
};

export const mapThreadsToGroup = (
  result: RequiredCategorySeekMaxThreads,
  language: CareerHubLanguage,
): MainGroupThread[] =>
  result?.edges.map(
    ({
      node: {
        id,
        slug,
        title,
        attachments,
        creationDate,
        pollV3,
        exactSocialData,
        author,
        description,
        comments,
        categoryGroup,
      },
    }) => {
      const threadSlug = formattingThreadSlug(slug, id);
      const groupSlug = convertToSlug(categoryGroup?.name || '');
      const url = getPagePaths({
        language,
        pathParams: { groupSlug, threadSlug },
      }).CommunityThreadDetail;
      return {
        id,
        description,
        slug: slug || '',
        title: title || '',
        author: mapAuthor(author),
        // eslint-disable-next-line @typescript-eslint/no-shadow
        attachments: attachments?.map(({ url }) => url) || [],
        exactSocialData: mapExactSocialData(exactSocialData),
        liked: false,
        shareLink: '',
        creationDate: creationDate?.shortLabel || '',
        poll: mapPollData(pollV3),
        totalComments: comments.length || 0,
        url,
      };
    },
  ) || [];

export const generateGroupThreads = (
  result: RequiredCategorySeekMaxThreads,
  language: CareerHubLanguage,
): CategoryThreadsResult => {
  const { totalCount = 0, pageInfo } = result || {};

  return {
    total: totalCount,
    totalPage: Math.ceil(totalCount / PAGE_SIZE),
    category: mapCategory(
      language,
      result?.edges?.[0]?.node?.categoryGroup || undefined,
    ),
    threads: mapThreadsToGroup(result, language),
    pageInfo: {
      endCursor: pageInfo?.endCursor || undefined,
      hasNextPage: pageInfo?.hasNextPage || false,
    },
  };
};

export const generateThreadDetail = (
  { seekMaxComments, seekMaxThread }: ThreadDetailQuery,
  language: CareerHubLanguage,
): Thread => {
  const {
    id,
    description,
    title,
    slug,
    shareLink,
    author,
    pollV3,
    personalisedDetails,
    creationDate,
    exactSocialData,
    categoryGroup,
  } = seekMaxThread || {};
  const threadSlug = formattingThreadSlug(slug, id);
  const groupSlug = convertToSlug(categoryGroup?.name || '');
  const url = `${
    getPagePaths({ language, pathParams: { threadSlug, groupSlug } })
      .CommunityThreadDetail
  }`;
  return {
    id: id || '',
    description: description || '',
    title: title || '',
    slug: threadSlug || '',
    shareLink: shareLink || '',
    author: mapAuthor(author),
    poll: mapPollData(pollV3),
    liked: personalisedDetails?.liked || false,
    creationDate: creationDate?.shortLabel || '',
    totalComments:
      seekMaxThread?.exactSocialData?.commentAndReplyCount?.count || 0,
    category: mapCategory(language, categoryGroup || undefined),
    // eslint-disable-next-line @typescript-eslint/no-shadow
    attachments: seekMaxThread?.attachments?.map(({ url }) => url) || [],
    url,
    exactSocialData: mapExactSocialData(
      exactSocialData || {
        likeCount: { count: 0, label: '0' },
        commentAndReplyCount: { count: 0, label: '0' },
        commentCount: { count: 0, label: '0' },
      },
    ),
    comments: seekMaxComments.comments.map(
      ({
        id: commendId,
        content,
        author: commentAuthor,
        creationDate: commentCreationDate,
        exactSocialData: commentExactSocialData,
        personalisedDetails: commentPersonalisedDetails,
      }) => ({
        id: commendId,
        content,
        author: mapAuthor(commentAuthor),
        creationDate: commentCreationDate.shortLabel,
        voted: commentPersonalisedDetails?.liked || false,
        exactSocialData: mapExactSocialCommentData(commentExactSocialData),
        liked: commentPersonalisedDetails?.liked || false,
      }),
    ),
  };
};

export const generateComments = (
  queryResult: SeekMaxThreadCommentsQuery,
): Comment[] => {
  if (
    queryResult &&
    queryResult.seekMaxComments &&
    queryResult.seekMaxComments.comments.length > 0
  ) {
    return queryResult.seekMaxComments.comments.map(
      ({
        id,
        content,
        author,
        creationDate,
        exactSocialData,
        personalisedDetails,
      }) => ({
        id,
        content,
        author: mapAuthor(author),
        creationDate: creationDate.shortLabel,
        exactSocialData: mapExactSocialCommentData(exactSocialData),
        liked: personalisedDetails?.liked || false,
      }),
    );
  }
  return [];
};

export const generateReplies = (queryResult: SeekMaxRepliesQuery): Reply[] => {
  if (
    queryResult &&
    queryResult.seekMaxReplies &&
    queryResult.seekMaxReplies.replies.length > 0
  ) {
    return queryResult.seekMaxReplies.replies.map(
      ({ id, content, author, creationDate }) => ({
        id,
        content,
        author: mapAuthor(author),
        creationDate: creationDate.shortLabel,
      }),
    );
  }
  return [];
};

export interface FeaturedThreadsResult {
  totalThread: number;
  pageInfo: {
    startCursor?: number | null;
    endCursor?: number | null;
    hasPrevPage: boolean;
    hasNextPage: boolean;
  };
  threads: ThreadCard[];
}

export const generateFeaturedThreads = (
  seekMaxThread: FeaturedSeekMaxThreadsV3Query,
  language: CareerHubLanguage,
): FeaturedThreadsResult => {
  if (
    seekMaxThread &&
    seekMaxThread.featuredSeekMaxThreadsV3 &&
    seekMaxThread.featuredSeekMaxThreadsV3.edges?.length > 0
  ) {
    const { totalCount, edges, pageInfo } =
      seekMaxThread.featuredSeekMaxThreadsV3;
    return {
      totalThread: totalCount || 0,
      pageInfo: {
        startCursor: pageInfo.startCursor,
        endCursor: pageInfo.endCursor,
        hasPrevPage: pageInfo.hasPrevPage,
        hasNextPage: pageInfo.hasNextPage,
      },
      threads: edges
        .map(({ node }) => {
          if (node) {
            const {
              id,
              description,
              author,
              pollV3,
              creationDate,
              exactSocialData,
              categoryGroup,
              attachments,
              title,
              slug,
              comments,
            } = node || {};
            const threadSlug = formattingThreadSlug(slug, id);
            const groupSlug = convertToSlug(categoryGroup?.name || '');
            const url = `${
              getPagePaths({ language, pathParams: { threadSlug, groupSlug } })
                .CommunityThreadDetail
            }`;
            return {
              id: id || '',
              description: description || '',
              title: title || '',
              slug: slug || '',
              author: mapAuthor(author),
              poll: mapPollData(pollV3),
              liked: false,
              creationDate: creationDate?.shortLabel || '',
              totalComments: comments.length || 0,
              category: mapCategory(language, categoryGroup || undefined),
              // eslint-disable-next-line @typescript-eslint/no-shadow
              attachments: attachments?.map(({ url }) => url) || [],
              exactSocialData: mapExactSocialData(
                exactSocialData || {
                  likeCount: { count: 0, label: '0' },
                  commentAndReplyCount: { count: 0, label: '0' },
                },
              ),
              url,
            };
          }
          return null;
        })
        .filter((thread) => thread !== null),
    };
  }
  return {
    totalThread: seekMaxThread?.featuredSeekMaxThreadsV3?.totalCount || 0,
    threads: [],
    pageInfo: {
      startCursor: null,
      endCursor: null,
      hasPrevPage: false,
      hasNextPage: false,
    },
  };
};
